import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X } from 'lucide-react';
import api from '../lib/api';

/**
 * App-wide broadcast banner that polls /api/broadcasts/recent every 12s.
 * When a NEW broadcast is detected (not seen before), it shows a full-width
 * animated banner at the top of /app/* pages.
 */
export default function BroadcastBanner() {
  const [active, setActive] = useState(null);
  const seenIds = useRef(new Set());
  const dismissed = useRef(new Set());

  useEffect(() => {
    let alive = true;
    let timeoutId;

    const poll = async () => {
      try {
        const { data } = await api.get('/broadcasts/recent');
        if (!alive || !data?.length) return;
        // Find newest broadcast we haven't seen + not dismissed
        for (const b of data) {
          if (!seenIds.current.has(b.id) && !dismissed.current.has(b.id)) {
            seenIds.current.add(b.id);
            setActive(b);
            // Auto-dismiss after 8 seconds
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => setActive(null), 8000);
            return;
          } else {
            seenIds.current.add(b.id);
          }
        }
      } catch {}
    };

    // First load: mark existing as seen to avoid replaying history
    api.get('/broadcasts/recent').then(({ data }) => {
      if (!alive) return;
      (data || []).forEach(b => seenIds.current.add(b.id));
    }).catch(() => {});

    const interval = setInterval(poll, 12000);
    return () => { alive = false; clearInterval(interval); clearTimeout(timeoutId); };
  }, []);

  if (!active) return null;

  const dismiss = () => { if (active) dismissed.current.add(active.id); setActive(null); };

  return (
    <div className="fixed top-16 left-0 right-0 z-50 px-3 broadcast-enter pointer-events-none">
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div className="relative rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.25), rgba(244,114,182,0.22), rgba(123,47,247,0.22), rgba(0,198,255,0.18))', border: '1px solid rgba(251,191,36,0.4)', backdropFilter: 'blur(20px)' }}>
          {/* Animated shimmer */}
          <div className="absolute inset-0 opacity-50" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', backgroundSize: '200% 100%', animation: 'shimmer 2.5s linear infinite' }} />

          <div className="relative flex items-center gap-3 p-3.5">
            <div className="text-4xl float-slow drop-shadow-[0_0_18px_rgba(251,191,36,0.6)]">{active.gift_emoji}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
                <Sparkles size={11} /> Cosmic Broadcast
              </div>
              <div className="text-sm md:text-base font-semibold mt-0.5">
                <span className="text-amber-300">{active.sender_name}</span>
                <span className="text-white/80"> gifted a </span>
                <span className="text-fuchsia-300 font-display font-bold">{active.gift_name}</span>
                <span className="text-white/80"> to </span>
                <span className="text-cyan-300">{active.recipient_name}</span>
                <span className="text-white/80">!</span>
              </div>
              <div className="text-[11px] text-white/60 mt-1 flex items-center gap-2">
                <span>{active.price.toLocaleString()} stars</span>
                <span>·</span>
                <span className="uppercase tracking-wider">Mythic</span>
              </div>
            </div>
            <button onClick={dismiss} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white" aria-label="Dismiss"><X size={16} /></button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes broadcast-enter {
          0% { opacity: 0; transform: translateY(-100%); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .broadcast-enter > div { animation: broadcast-enter 600ms cubic-bezier(0.2, 0.8, 0.2, 1); }
      `}</style>
    </div>
  );
}
